/**
 * @fileoverview Reusable loading spinner component with consistent sizing and centering.
 */

import { Box, CircularProgress, type SxProps, type Theme } from '@mui/material';
import { SPINNER_SIZE } from '@/config/constants';

export interface LoadingSpinnerProps {
  /**
   * Size variant for the spinner
   * @default 'default'
   */
  size?: 'small' | 'default' | number;
  /**
   * Whether to center the spinner in its container
   * @default true
   */
  centered?: boolean;
  /**
   * Additional sx props for the container (only applies when centered=true)
   */
  sx?: SxProps<Theme>;
}

/**
 * Displays a loading spinner with consistent sizing.
 * Optionally centers the spinner with flexbox layout.
 */
export function LoadingSpinner({
  size = 'default',
  centered = true,
  sx,
}: LoadingSpinnerProps) {
  const spinnerSize = typeof size === 'number'
    ? size
    : size === 'small'
      ? SPINNER_SIZE.SMALL
      : SPINNER_SIZE.DEFAULT;

  const spinner = <CircularProgress size={spinnerSize} />;

  if (!centered) {
    return spinner;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, ...sx }}>
      {spinner}
    </Box>
  );
}
